import React, { FC, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Edit, Undo } from '@mui/icons-material';
import { InputAdornment, TextField, Tooltip } from '@mui/material';
import { toast } from 'react-toastify';
import DropdownSearchSelect, { IDropdownSelectOption } from 'components/DropdownSearchSelect/DropdownSearchSelect';
import TextInput from 'components/TextInput/TextInput';
import { accessTokenPermissions } from 'helpers/auth0';
import {
  PARTNER_USER_ENABLED,
  PARTNER_USER_REASON_NO_ANSWER,
  partnerUserDecisionOptions,
  partnerUserReasonText,
} from 'pages/CreateApplication/constants';
import { useAppSelector } from 'reduxState/hooks';
import { useGetAllAdminApplicationsQuery, useGetUserApplicationsQuery } from 'reduxState/store/application/api';
import {
  useGetPartnerUserConfigQuery,
  useSavePartnerUserConfigMutation,
} from 'reduxState/store/applicationManagement/api';
import { PartnerUserConfig } from 'reduxState/store/applicationManagement/types';
import { selectUserToken } from 'reduxState/store/user/slice';
import { ConfirmEdit } from './ConfirmEdit';
import '../scss/AppPartnerUser.scss';

// Granted only to the small internal group that may change partner_user after app creation (MCH-1888).
export const CREATE_PARTNER_USER_CONFIG_PERMISSION = 'create:application_partner_user_config';
export const UPDATE_PARTNER_USER_CONFIG_PERMISSION = 'update:application_partner_user_config';

const NO_DECISION_TEXT = "No decision: follows its partner's setting";
const NO_PERMISSION_TEXT = "You don't have permission to view this setting.";
const LOAD_ERROR_TEXT = "Couldn't load this setting. Refresh the page to try again.";
const LOADING_TEXT = 'Loading...';

export interface AppPartnerUserProps {
  applicationId: string;
}

const decisionLabel = (enabled: boolean): string =>
  partnerUserDecisionOptions.find(option => (option.value === PARTNER_USER_ENABLED) === enabled)?.label ?? '';

const describeConfig = (config: PartnerUserConfig | null | undefined, isPartnerApp: boolean): string => {
  if (isPartnerApp) {
    if (!config) return 'Partner switch: partner_user is not enabled for this partner';
    return `Partner switch: partner_user is ${config.PartnerUserEnabled ? 'on' : 'off'} for every app under this partner`;
  }
  if (!config) return NO_DECISION_TEXT;
  return decisionLabel(config.PartnerUserEnabled);
};

// Guard against missing fields so one odd row can't crash the whole Application Management page
const describeChange = (config: PartnerUserConfig): string => {
  const changedOn = config.ModifiedDate?.slice(0, 10) ?? 'unknown date';
  return `Reason: ${config.Reason ?? ''}\nLast changed by ${config.ModifiedAuthor ?? 'unknown'} on ${changedOn}`;
};

export const AppPartnerUser: FC<React.PropsWithChildren<AppPartnerUserProps>> = ({ applicationId }) => {
  const { user } = useAuth0();
  const accessToken = useAppSelector(selectUserToken);
  const {
    data: config,
    error,
    isLoading: isLoadingConfig,
    isSuccess: isConfigLoaded,
    isFetching: isFetchingConfig,
  } = useGetPartnerUserConfigQuery({ appId: applicationId });
  const { data: userApplications } = useGetUserApplicationsQuery();
  const { data: adminApplications } = useGetAllAdminApplicationsQuery();
  const [savePartnerUserConfig, { isLoading }] = useSavePartnerUserConfigMutation();
  const [isEditing, setEditing] = useState(false);
  const [decision, setDecision] = useState<IDropdownSelectOption | null>(null);
  const [reason, setReason] = useState('');

  // A payable (admin) app's row is the on/off switch for every app under that partner, so it's
  // shown read-only here: switching a whole partner is a deliberate, reviewed change. The full payable
  // list is the authority; the user's own app groups are a backstop.
  const isPartnerApp =
    !!adminApplications?.some(adminApp => String(adminApp.AdminAppID) === applicationId) ||
    !!userApplications?.appGroups.some(group => String(group.adminId) === applicationId);
  const requiredPermission = config ? UPDATE_PARTNER_USER_CONFIG_PERMISSION : CREATE_PARTNER_USER_CONFIG_PERMISSION;
  const isForbidden = !!error && typeof error === 'object' && 'status' in error && error.status === 403;
  const hasLoadError = !!error && !isForbidden;
  // Fail closed: edit only once the app's row and the payable list have loaded. A slow or failed load
  // would otherwise look like "no decision" (saving would POST a second row) or hide a partner app.
  const canEdit =
    isConfigLoaded &&
    !isFetchingConfig &&
    !error &&
    !!adminApplications &&
    !isPartnerApp &&
    accessTokenPermissions(accessToken).includes(requiredPermission);

  const startEdit = () => {
    setDecision(null);
    setReason('');
    setEditing(true);
  };

  const cancelEdit = () => {
    if (isLoading) return;
    setEditing(false);
  };

  const handleSave = async () => {
    if (!decision) return;
    try {
      await savePartnerUserConfig({
        appId: applicationId,
        configId: config?.ID,
        partnerUserEnabled: decision.value === PARTNER_USER_ENABLED,
        reason: reason.trim(),
        author: user?.email ?? 'missing email',
      }).unwrap();
      toast.success('Partner user setting updated successfully');
      setEditing(false);
    } catch (saveError: unknown) {
      let errorMessage = 'Failed to update the partner user setting. Please try again.';
      if (saveError && typeof saveError === 'object') {
        const e = saveError as { data?: { ErrorMessage?: string; message?: string }; status?: number | string };
        if (e.status === 403) {
          errorMessage = "Failed to update the partner user setting: you don't have permission.";
        } else if (e.data?.ErrorMessage) {
          errorMessage = `Failed to update the partner user setting: ${e.data.ErrorMessage}`;
        } else if (e.data?.message) {
          errorMessage = `Failed to update the partner user setting: ${e.data.message}`;
        }
      }
      toast.error(errorMessage);
      // Keep edit mode open so the operator can retry or cancel.
    }
  };

  let currentText = describeConfig(config, isPartnerApp);
  if (isForbidden) currentText = NO_PERMISSION_TEXT;
  else if (hasLoadError) currentText = LOAD_ERROR_TEXT;
  else if (isLoadingConfig) currentText = LOADING_TEXT;
  const fieldText = config && !error ? `${currentText}\n${describeChange(config)}` : currentText;
  const isValid = decision !== null && reason.trim() !== '';
  const before = config ? decisionLabel(config.PartnerUserEnabled) : NO_DECISION_TEXT;
  const after = decision?.label ?? '';
  // Same reason question and example as the Create Application form, following the chosen answer
  const reasonText = partnerUserReasonText[decision?.value ?? PARTNER_USER_REASON_NO_ANSWER];

  return (
    <div className="app-partner-user settings-form-group">
      <TextField
        id="appPartnerUser"
        className={`app-partner-user-field ${isEditing ? 'user-editing' : ''}`}
        variant="outlined"
        label="Partner User"
        multiline
        disabled
        value={fieldText}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          // Keep Undo while editing even if a background refetch briefly blocks starting a new edit
          endAdornment: canEdit || isEditing ? (
            <InputAdornment position="start">
              {isEditing ? (
                <Tooltip title="Cancel editing">
                  <Undo onClick={cancelEdit} className="edit-icon" data-testid="partner-user-undo-icon" />
                </Tooltip>
              ) : (
                <Edit onClick={startEdit} className="edit-icon" data-testid="partner-user-edit-icon" />
              )}
            </InputAdornment>
          ) : undefined,
        }}
      />
      {isEditing && (
        <div className="partner-user-edit">
          <DropdownSearchSelect
            label="Partner User"
            value={decision}
            options={partnerUserDecisionOptions}
            handleChange={setDecision}
            placeholder="Will this app send a partner user ID for each user?"
            isDisabled={isLoading}
          />
          <TextInput
            label={reasonText.label}
            placeholderText={reasonText.placeholder}
            value={reason}
            handleChange={(value: string) => setReason(value)}
            isDisabled={isLoading}
          />
          {/* Always shown so it's clear what the edit leads to; usable once an answer and reason are given */}
          <ConfirmEdit
            headerText="Edit Partner User"
            confirmText="Confirm save to update this app's partner user setting:"
            changeText={`Partner user: ${before} → ${after}`}
            onSave={handleSave}
            isDisabled={!isValid || isLoading}
          />
        </div>
      )}
    </div>
  );
};
